/**
 * The open playEngine version, held once, addressed by path.
 *
 * WHAT IT REPLACES. Every edit used to deep-clone the whole version and publish
 * the copy to React's root, so a keystroke cost what the playable weighed. Here
 * a write copies the nodes on its path (see modules/setIn) and wakes only the
 * subscribers whose slice it touched.
 *
 * WHAT A DOCUMENT IS. The version object itself, not its config — the panels
 * already write paths rooted there ("playEngineConfig.components.3.config.text",
 * "dashboardMeta.gameFlow"), so nothing has to be re-spelled to use this.
 *
 * FOUR PIECES OF STATE, and they are not the same thing:
 *
 *   root      what the editor shows and the preview runs
 *   base      what the server last confirmed — the thing a conflict rebases onto
 *   dirty     paths written locally and not yet sent
 *   inFlight  paths sent and not yet acknowledged
 *   revision  the version's configRevision, as last read from the server
 *
 * `dirty` and `inFlight` are separate because a field can be typed into again
 * while its previous value is still on the wire. Acknowledging the request must
 * settle the value that WAS sent and leave the newer one owed — collapsing the
 * two would drop the second edit and leave `base` describing something the
 * server does not hold, which is the state a conflict rebases against.
 *
 * `dirty` is what makes an external write safe to apply: an engine echo, a poll
 * or a 409 reload may refresh anything the user has not touched, and must never
 * overwrite something they have. Custom Variables approximates this today with a
 * two-second timestamp window, which loses the race whenever a save is slow.
 *
 * NORMAL PLAYABLES DO NOT USE THIS. Their panels mutate config subtrees in place
 * (containerVersionSettings deletes keys straight out of props.selectedVersion),
 * and structural sharing would carry those mutations into objects that are
 * supposed to be older snapshots. They keep the deep clone; see home.js.
 */

import _ from "lodash";
import { getIn, pathsOverlap, setIn, toDotted } from "./setIn";

/** Documents kept alive at once. One is open at a time; the rest are slack. */
const MAX_DOCS = 4;

/** @type {Map<string, object>} insertion-ordered, so the oldest evicts first */
const docs = new Map();

function createDoc(key, version) {
  return {
    key,
    root: version,
    base: version,
    revision: Number(_.get(version, "configRevision", 0)) || 0,
    /** @type {Map<string, *>} dotted path → value written, not yet sent */
    dirty: new Map(),
    /** @type {Map<string, *>} dotted path → value sent, not yet acknowledged */
    inFlight: new Map(),
    /** @type {Map<string, Set<Function>>} dotted path → listeners */
    listeners: new Map(),
  };
}

function evictOldest() {
  while (docs.size > MAX_DOCS) {
    const oldest = docs.keys().next();
    if (oldest.done) return;
    docs.delete(oldest.value);
  }
}

/**
 * Open `key` on `version`, or adopt a copy that arrived from elsewhere.
 *
 * Called before every write rather than from a lifecycle hook, so there is no
 * ordering to get wrong. A version object that is not the one this store
 * produced came from outside — a version switch, an agent run, a reload — and
 * replaces what is held, including any unsaved paths. Discarding those is the
 * honest outcome: the external writer has already saved, and merging a
 * half-typed field into their document would be a guess.
 *
 * @param {string} key version id
 * @param {object} version
 * @returns {object} the document
 */
export function syncDoc(key, version) {
  const existing = docs.get(key);
  if (existing && existing.root === version) return existing;

  if (existing) {
    // Keep the listeners: the fields subscribed to this document are still on
    // screen and still want to hear about the values that just replaced theirs.
    const listeners = existing.listeners;
    const replaced = createDoc(key, version);
    replaced.listeners = listeners;
    docs.set(key, replaced);
    notifyPath(replaced, "");
    return replaced;
  }

  const doc = createDoc(key, version);
  docs.set(key, doc);
  evictOldest();
  return doc;
}

/** @returns {object|undefined} */
export function getDoc(key) {
  return docs.get(key);
}

/** @returns {*} the live document root, or undefined when nothing is open */
export function getRoot(key) {
  const doc = docs.get(key);
  return doc && doc.root;
}

/** Read one path off the live root. */
export function readPath(key, path, fallback) {
  return getIn(getRoot(key), path, fallback);
}

function notifyPath(doc, dottedPath) {
  for (const [subscribedPath, listeners] of doc.listeners) {
    if (!pathsOverlap(subscribedPath, dottedPath)) continue;
    for (const listener of [...listeners]) listener(dottedPath);
  }
}

/**
 * Write one path and wake the subscribers it affects.
 *
 * @param {string} key
 * @param {string|Array} path
 * @param {*} value
 * @returns {*} the new root
 */
export function writePath(key, path, value) {
  const doc = docs.get(key);
  if (!doc) return undefined;

  const dotted = toDotted(path);
  doc.root = setIn(doc.root, path, value);

  // A write higher up subsumes everything queued below it: the save will carry
  // the wider value, and leaving the narrower paths queued would re-send stale
  // leaves of a branch that no longer exists.
  if (dotted === "") {
    doc.dirty.clear();
  } else {
    for (const queued of [...doc.dirty.keys()]) {
      if (queued !== dotted && queued.startsWith(`${dotted}.`)) doc.dirty.delete(queued);
    }
  }
  const isSubsumed = [...doc.dirty.keys()].some(
    (queued) => queued !== dotted && dotted.startsWith(`${queued}.`)
  );
  if (!isSubsumed) doc.dirty.set(dotted, value);

  notifyPath(doc, dotted);
  return doc.root;
}

/**
 * Replace the whole document — a preset applied, an agent run, a 409 reload.
 * Equivalent to writing the root path, and named so call sites read as what
 * they are.
 *
 * @param {string} key
 * @param {object} nextRoot
 * @returns {object} the new root
 */
export function replaceRoot(key, nextRoot) {
  return writePath(key, "", nextRoot);
}

/**
 * Subscribe to a slice. The listener fires for writes at `path`, inside it, or
 * to any ancestor of it — see `pathsOverlap`.
 *
 * @param {string} key
 * @param {string|Array} path "" for the whole document
 * @param {Function} listener
 * @returns {Function} unsubscribe
 */
export function subscribe(key, path, listener) {
  const doc = docs.get(key);
  if (!doc) return () => {};

  const dotted = toDotted(path);
  if (!doc.listeners.has(dotted)) doc.listeners.set(dotted, new Set());
  doc.listeners.get(dotted).add(listener);

  return () => {
    const listeners = doc.listeners.get(dotted);
    if (!listeners) return;
    listeners.delete(listener);
    if (listeners.size === 0) doc.listeners.delete(dotted);
  };
}

/** Paths written locally and not yet sent. */
export function getDirtyPaths(key) {
  const doc = docs.get(key);
  return doc ? [...doc.dirty.keys()] : [];
}

/** Paths sent and still awaiting an answer. */
export function getInFlightPaths(key) {
  const doc = docs.get(key);
  return doc ? [...doc.inFlight.keys()] : [];
}

/**
 * True when `path`, or anything containing it, is still owed to the server —
 * queued or on the wire. The gate an incoming external value has to pass before
 * it is applied, so it has to cover both: a value whose save is in flight has
 * not been acknowledged either, and an echo must not overwrite it.
 *
 * @param {string} key
 * @param {string|Array} path
 * @returns {boolean}
 */
export function isDirty(key, path) {
  const doc = docs.get(key);
  if (!doc) return false;
  const dotted = toDotted(path);
  for (const queued of doc.dirty.keys()) {
    if (pathsOverlap(queued, dotted)) return true;
  }
  for (const queued of doc.inFlight.keys()) {
    if (pathsOverlap(queued, dotted)) return true;
  }
  return false;
}

/** The revision this document was last read at. */
export function getRevision(key) {
  const doc = docs.get(key);
  return doc ? doc.revision : 0;
}

/**
 * Hand the queued paths to a save and mark them as on the wire.
 *
 * One request at a time: while a save is unanswered a second would race it to
 * the same paths, and the revision guard would refuse whichever lost. Returns
 * null when a save is already open, or when nothing is owed.
 *
 * @param {string} key
 * @returns {{ paths: string[], fields: object, revision: number }|null}
 */
export function beginSave(key) {
  const doc = docs.get(key);
  if (!doc) return null;
  if (doc.inFlight.size > 0) return null;
  if (doc.dirty.size === 0) return null;

  doc.inFlight = doc.dirty;
  doc.dirty = new Map();

  const fields = {};
  for (const [path, value] of doc.inFlight) fields[path] = value;

  return { paths: [...doc.inFlight.keys()], fields, revision: doc.revision };
}

/**
 * The save landed. `base` takes the values that were SENT — not what the root
 * holds now, which may already carry a keystroke the server has never seen.
 *
 * @param {string} key
 * @param {{ revision?: number }} [ack]
 */
export function commitSave(key, { revision } = {}) {
  const doc = docs.get(key);
  if (!doc) return;

  let nextBase = doc.base;
  for (const [path, value] of doc.inFlight) {
    nextBase = path === "" ? value : setIn(nextBase, path, value);
  }
  doc.base = nextBase;
  doc.inFlight = new Map();

  if (Number.isFinite(revision)) doc.revision = revision;
}

/**
 * The save failed and its paths are owed again.
 *
 * A path written since it went out keeps the newer value: the request carried
 * the older one, so restoring it would undo an edit the user has already made.
 *
 * @param {string} key
 */
export function rollbackSave(key) {
  const doc = docs.get(key);
  if (!doc) return;

  for (const [path, value] of doc.inFlight) {
    if (!doc.dirty.has(path)) doc.dirty.set(path, value);
  }
  doc.inFlight = new Map();
}

/**
 * Adopt a document the server handed back, keeping the local edits it does not
 * contradict — the 409 rebase.
 *
 * A path is replayed when the other writer left it alone, which is answerable
 * because `base` is what they started from. A path they also changed is a real
 * collision: theirs wins and ours is named to the caller, because silently
 * keeping either one is how an edit disappears without trace.
 *
 * @param {string} key
 * @param {object} serverRoot the document as the server now holds it
 * @param {{ revision?: number }} [options]
 * @returns {{ replayed: string[], collided: string[] }}
 */
export function rebaseOnServer(key, serverRoot, { revision } = {}) {
  const doc = docs.get(key);
  if (!doc || !serverRoot) return { replayed: [], collided: [] };

  // Everything still owed, in the order it was written: what was on the wire
  // was rejected, so it is owed again alongside anything typed since.
  const owed = new Map();
  for (const [path, value] of doc.inFlight) owed.set(path, value);
  for (const [path, value] of doc.dirty) owed.set(path, value);

  const replayed = [];
  const collided = [];
  let nextRoot = serverRoot;

  for (const [path, value] of owed) {
    const untouched = _.isEqual(getIn(doc.base, path), getIn(serverRoot, path));
    if (untouched) {
      replayed.push(path);
      nextRoot = path === "" ? value : setIn(nextRoot, path, value);
    } else {
      collided.push(path);
    }
  }

  doc.root = nextRoot;
  doc.base = serverRoot;
  doc.inFlight = new Map();
  doc.dirty = new Map();
  for (const path of replayed) doc.dirty.set(path, owed.get(path));
  if (Number.isFinite(revision)) doc.revision = revision;

  notifyPath(doc, "");
  return { replayed, collided };
}

/** What the server last confirmed — the document a 409 rebases against. */
export function getBase(key) {
  const doc = docs.get(key);
  return doc && doc.base;
}

/** Drop a document. Listeners go with it. */
export function closeDoc(key) {
  docs.delete(key);
}

/** Tests only. */
export function resetDocStore() {
  docs.clear();
}
